// Libraries
import {
  // Functions
  functions,
  httpsCallable,
  getDoc,
  getDocs,
  query,
  collection,
  where,
  increment,
  serverTimestamp,
  writeBatch,
  doc,
  firestore,
  deleteDoc,
  limit,
  // Storage
  storage,
  ref,
  getDownloadURL,
  getMetadata,
  getBytes,
  uploadBytes,
  deleteObject,
} from "@/firebase/init";
import moment from "moment-timezone";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Buffer } from "buffer";

import store from "@/store/index.js";

class LeaflinkClient {
  constructor(orderID, mytraceOrder) {
    this.baseURL = "https://app.leaflink.com/";
    this.orderID = orderID;
    this.mytraceOrder = mytraceOrder;
    this.payableTo = null;
    this.lineItems = [];
    this.getPayableTo();
    this.status = [
      {
        text: "Submitted",
        value: "submitted",
        mytrace: "submitted",
      },
      {
        text: "Accepted",
        value: "accepted",
        mytrace: "accepted",
      },
      {
        text: "Fulfilled",
        value: "fulfilled",
        mytrace: "fulfilled",
      },
      {
        text: "Shipped",
        value: "shipped",
        mytrace: "shipped",
      },
      {
        text: "Complete",
        value: "complete",
        mytrace: "complete",
      },
      {
        text: "Cancelled",
        value: "cancelled",
        mytrace: "cancelled",
      },
      {
        text: "Rejected",
        value: "rejected",
        mytrace: "rejected",
      },
    ];
  }

  // mytrace required data
  async getPayableTo() {
    const payableToRef = collection(firestore, "payableTo");
    const state = where(
        "location.state.stateID",
        "==",
        this.mytraceOrder.location.shipping.state.stateID
      ),
      brand = where("brandID", "==", this.mytraceOrder.brand.id);
    const q = query(payableToRef, state, brand);
    const response = await getDocs(q, limit(1));

    this.payableTo = {
      id: response.docs[0].id,
      ...response.docs[0].data(),
    };
  }

  // Leaflink
  async getOrders(params) {
    let orders = [],
      response = null,
      nextQuery = null;

    if (!this.payableTo) {
      await this.getPayableTo();
    }
    let queryURL = `${this.baseURL}api/v2/orders-received/?customer=${params.accountID}&include_children=line_items,customer&status__not=draft,combined,backorder&limit=500`;
    if (params.createdDate) {
      queryURL += `&created_on__gte=${params.createdDate}`;
    }
    if (params.brandID) {
      queryURL += `&brand=${params.brandID}`;
    }

    try {
      const leaflinkOrders = await httpsCallable(functions, "externalAPI");
      do {
        queryURL = nextQuery ? nextQuery : queryURL;

        const { data } = await leaflinkOrders({
          method: "GET",
          headers: {
            Authorization: `App ${this.payableTo.integrations.leaflink.api}`,
          },
          url: queryURL,
        });

        response = data;
        nextQuery = response.next ? response.next : null;
        data.results.forEach((order) => {
          orders.push(order);
        });
      } while (response.next);

      return {
        orders,
      };
    } catch (error) {
      console.log(error);
      throw new Error("Could not load sales orders");
    }
  }
  async getOrder() {
    const leaflinkOrder = await httpsCallable(functions, "externalAPI");
    if (!this.payableTo) {
      await this.getPayableTo();
    }
    const { data } = await leaflinkOrder({
      method: "GET",
      headers: {
        Authorization: `App ${this.payableTo.integrations.leaflink.api}`,
      },
      url: `${process.env.VUE_APP_LEAFLINK}api/v2/orders-received/${this.orderID}`,
    });
    return data;
  }
  async getProductLines() {
    let productLines = [];
    if (!this.payableTo) {
      await this.getPayableTo();
    }

    try {
      const getProductLines = await httpsCallable(functions, "externalAPI");
      const { data } = await getProductLines({
        method: "GET",
        headers: {
          Authorization: `App ${this.payableTo.integrations.leaflink.api}`,
        },
        url: `${process.env.VUE_APP_LEAFLINK}api/v2/product-lines`,
      });
      console.log(data);

      return productLines;
    } catch (error) {
      throw new Error(error.message);
    }
  }
  async getLineItems() {
    if (!this.payableTo) {
      await this.getPayableTo();
    }
    const getExternalLineItems = await httpsCallable(functions, "externalAPI");
    const { data } = await getExternalLineItems({
      method: "GET",
      headers: {
        Authorization: `App ${this.payableTo.integrations.leaflink.api}`,
      },
      url: `${process.env.VUE_APP_LEAFLINK}api/v2/orders-received/${this.orderID}/line-items`,
    });
    this.lineItems = data.results.map((item) => {
      return {
        id: item.id,
        productID: item.product,
      };
    });
    return this.lineItems;
  }

  async getPayments() {
    if (!this.payableTo) {
      await this.getPayableTo();
    }
    const getExternalPayments = await httpsCallable(functions, "externalAPI");
    const { data } = await getExternalPayments({
      method: "GET",
      headers: {
        Authorization: `App ${this.payableTo.integrations.leaflink.api}`,
      },
      url: `${process.env.VUE_APP_LEAFLINK}api/v2/orders-received/${this.orderID}/payments`,
    });
    return data.results.map((payment) => {
      return {
        id: payment.id,
        amount: Number(payment.total.amount),
        type: payment.payment_type,
      };
    });
  }

  async getUsers() {
    if (!this.payableTo) {
      await this.getPayableTo();
    }
    if (
      this.payableTo.flags?.external &&
      this.payableTo.externalPlatform === "leaflink"
    ) {
      const leaflinkStaff = await httpsCallable(functions, "externalAPI");
      const { data } = await leaflinkStaff({
        method: "GET",
        headers: {
          Authorization: `App ${this.payableTo.integrations.leaflink.api}`,
        },
        url: `${process.env.VUE_APP_LEAFLINK}api/v2/company-staff/?is_active=true`,
      });
      return data;
    }
    return null;
  }

  async getProducts() {
    if (!this.payableTo) {
      await this.getPayableTo();
    }
    let products = [];
    let productsResponse;
    let nextQuery = null;

    const leaflinkProducts = httpsCallable(functions, "externalAPI");

    do {
      let queryURL = nextQuery
        ? nextQuery
        : `${process.env.VUE_APP_LEAFLINK}api/v2/products/?brand=${this.payableTo.integrations.leaflink.brandID}&archived=false&listing_state=Available,Internal`;

      const { data } = await leaflinkProducts({
        method: "GET",
        headers: {
          Authorization: `App ${this.payableTo.integrations.leaflink.api}`,
        },
        url: queryURL,
      });

      productsResponse = data;
      nextQuery = productsResponse.next ? productsResponse.next : null;
      data.results.forEach((product) => {
        products.push(product);
      });
    } while (productsResponse.next);

    return products;
  }

  async getContacts(params) {
    if (!this.payableTo) {
      await this.getPayableTo();
    }

    try {
      const leaflinkContacts = await httpsCallable(functions, "externalAPI");
      const { data } = await leaflinkContacts({
        method: "GET",
        headers: {
          Authorization: `App ${this.payableTo.integrations.leaflink.api}`,
        },
        url: `${this.baseURL}api/v2/customers/${params.leaflinkID}?include_children=contacts`,
      });

      if (data) {
        return {
          contacts: data.contacts,
        };
      }
    } catch (error) {
      throw new Error(error.message);
    }
  }

  // POST
  async createPayment(paymentData) {
    if (!this.payableTo) {
      await this.getPayableTo();
    }
    try {
      const createPayment = await httpsCallable(functions, "externalAPI");
      const { data } = await createPayment({
        method: "POST",
        headers: {
          Authorization: `App ${this.payableTo.integrations.leaflink.api}`,
        },
        url: `${process.env.VUE_APP_LEAFLINK}api/v2/order-payments/`,
        data: paymentData,
      });
      return data;
    } catch (error) {
      throw new Error(error.message);
    }
  }

  async transitionOrder(transition) {
    if (!this.payableTo) {
      await this.getPayableTo();
    }
    try {
      const updateOrderStatus = await httpsCallable(functions, "externalAPI");
      const { data } = await updateOrderStatus({
        method: "POST",
        headers: {
          Authorization: `App ${this.payableTo.integrations.leaflink.api}`,
        },
        url: `${process.env.VUE_APP_LEAFLINK}api/v2/orders-received/${this.orderID}/transition/${transition}/`,
      });
    } catch (e) {
      console.log(e);
      throw new Error(e);
    }
  }

  async updateOrderData(patchData) {
    if (!this.payableTo) {
      await this.getPayableTo();
    }
    try {
      const updateOrderData = await httpsCallable(functions, "externalAPI");
      const { data } = await updateOrderData({
        method: "PATCH",
        headers: {
          Authorization: `App ${this.payableTo.integrations.leaflink.api}`,
        },
        url: `${process.env.VUE_APP_LEAFLINK}api/v2/orders-received/${this.orderID}/`,
        data: patchData,
      });
      return data;
    } catch (e) {
      console.log(e);
      throw new Error(e);
    }
  }

  async updateLineItem(lineItem, patchData) {
    if (!this.payableTo) {
      await this.getPayableTo();
    }
    try {
      const updateLineItem = await httpsCallable(functions, "externalAPI");
      const { data } = await updateLineItem({
        method: "PATCH",
        headers: {
          Authorization: `App ${this.payableTo.integrations.leaflink.api}`,
        },
        url: `${process.env.VUE_APP_LEAFLINK}api/v2/line-items/${lineItem.id}`,
        data: patchData,
      });
    } catch (e) {
      console.error(e);
      throw new Error(e);
    }
  }

  async createLineItem(lineItem) {
    if (!this.payableTo) {
      await this.getPayableTo();
    }
    console.log("creating line item", lineItem);
    const createLineItem = await httpsCallable(functions, "externalAPI");
    const { data } = await createLineItem({
      method: "POST",
      headers: {
        Authorization: `App ${this.payableTo.integrations.leaflink.api}`,
      },
      url: `${process.env.VUE_APP_LEAFLINK}api/v2/line-items/`,
      data: {
        order: this.orderID,
        product: lineItem.externalIDs.leaflink,
        quantity: lineItem.qty,
        ordered_unit_price: {
          amount:
            lineItem.price.normal < lineItem.amount
              ? lineItem.amount
              : lineItem.price.normal,
          currency: "USD",
        },
        sale_price: {
          amount: lineItem.amount,
          currency: "USD",
        },
        notes: lineItem.notes,
        is_sample: lineItem.sample,
      },
    });

    return data;
  }

  async deleteLineItem(lineItem) {
    if (!this.payableTo) {
      await this.getPayableTo();
    }
    try {
      const deleteLineItem = await httpsCallable(functions, "externalAPI");
      const { data } = await deleteLineItem({
        method: "DELETE",
        headers: {
          Authorization: `App ${this.payableTo.integrations.leaflink.api}`,
        },
        url: `${process.env.VUE_APP_LEAFLINK}api/v2/line-items/${lineItem.id}`,
      });
      return data;
    } catch (e) {
      console.log(e);
      throw new Error(e);
    }
  }
}

export default LeaflinkClient;
